// Libraries
import React, { useState, useCallback, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';

// Styles
import './LoadingContainer.css';

// Types
export interface LoadingState {
    id: string;
    message?: string;
    type?: 'spinner' | 'progress' | 'dots' | 'pulse';
    overlay?: boolean;
    progress?: number; // 0-100 for progress bars
    cancelable?: boolean;
    onCancel?: () => void;
}

export interface LoadingContainerHandle {
    show: (options?: Partial<LoadingState>) => string;
    hide: (id?: string) => void;
    hideAll: () => void;
    updateProgress: (id: string, progress: number) => void;
    updateMessage: (id: string, message: string) => void;
    isLoading: () => boolean;
}

interface LoadingItemProps {
    loading: LoadingState;
    onCancel?: (id: string) => void;
}

// Individual loading component
const LoadingItem: React.FC<LoadingItemProps> = ({ loading, onCancel }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Trigger entrance animation
        setTimeout(() => setIsVisible(true), 50);
    }, []);

    const handleCancel = () => {
        if (loading.onCancel) {
            loading.onCancel();
        }
        if (onCancel) {
            onCancel(loading.id);
        }
    };

    const renderSpinner = () => {
        switch (loading.type) {
            case 'progress':
                return (
                    <div className="loading-progress-container">
                        <div className="progress mb-3" style={{ height: '8px' }}>
                            <div 
                                className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                role="progressbar"
                                style={{ width: `${loading.progress || 0}%` }}
                                aria-valuenow={loading.progress || 0}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            />
                        </div>
                        <div className="text-center small text-muted">
                            {loading.progress ? `${Math.round(loading.progress)}%` : 'Processing...'}
                        </div>
                    </div>
                );

            case 'dots':
                return (
                    <div className="loading-dots">
                        <div className="loading-dot"></div>
                        <div className="loading-dot"></div>
                        <div className="loading-dot"></div>
                    </div>
                );

            case 'pulse':
                return (
                    <div className="loading-pulse">
                        <div className="loading-pulse-ring"></div>
                        <div className="loading-pulse-ring"></div>
                        <div className="loading-pulse-ring"></div>
                    </div>
                );

            default: // 'spinner'
                return (
                    <div className="spinner-border spinner-border-lg text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                );
        }
    };

    return (
        <div className={`loading-item ${loading.overlay ? 'loading-overlay' : 'loading-inline'} ${isVisible ? 'loading-visible' : ''}`}>
            <div className="loading-content">
                <div className="loading-spinner-container">
                    {renderSpinner()}
                </div>
                
                {loading.message && (
                    <div className="loading-message mt-3">
                        <p className="mb-0 text-muted">{loading.message}</p>
                    </div>
                )}

                {loading.cancelable && (
                    <div className="loading-actions mt-3">
                        <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleCancel}
                        >
                            <i className="bi bi-x-circle me-2"></i>
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Main loading container component
const LoadingContainer = forwardRef<LoadingContainerHandle>((props, ref) => {
    const [loadingStates, setLoadingStates] = useState<LoadingState[]>([]);
    const counterRef = useRef(0);

    const show = useCallback((options: Partial<LoadingState> = {}) => {
        const id = `loading-${counterRef.current++}`;
        const newLoading: LoadingState = {
            id,
            message: 'Loading...',
            type: 'spinner',
            overlay: true,
            cancelable: false,
            ...options
        };

        setLoadingStates(prev => {
            // If overlay loading, replace any existing overlay loading
            if (newLoading.overlay) {
                return [newLoading, ...prev.filter(l => !l.overlay)];
            }
            return [newLoading, ...prev];
        });

        return id;
    }, []);

    const hide = useCallback((id?: string) => {
        if (!id) {
            // Hide the most recent loading state
            setLoadingStates(prev => prev.slice(1));
        } else {
            setLoadingStates(prev => prev.filter(loading => loading.id !== id));
        }
    }, []);

    const hideAll = useCallback(() => {
        setLoadingStates([]);
    }, []);

    const updateProgress = useCallback((id: string, progress: number) => {
        setLoadingStates(prev => prev.map(loading => 
            loading.id === id 
                ? { ...loading, progress: Math.max(0, Math.min(100, progress)) }
                : loading
        ));
    }, []);

    const updateMessage = useCallback((id: string, message: string) => {
        setLoadingStates(prev => prev.map(loading => 
            loading.id === id 
                ? { ...loading, message }
                : loading
        ));
    }, []);

    const isLoading = useCallback(() => {
        return loadingStates.length > 0;
    }, [loadingStates]);

    const handleCancel = useCallback((id: string) => {
        hide(id);
    }, [hide]);

    useImperativeHandle(ref, () => ({
        show,
        hide,
        hideAll,
        updateProgress,
        updateMessage,
        isLoading
    }), [show, hide, hideAll, updateProgress, updateMessage, isLoading]);

    // Separate overlay and inline loading states
    const overlayLoading = loadingStates.filter(l => l.overlay);
    const inlineLoading = loadingStates.filter(l => !l.overlay);

    return (
        <>
            {/* Overlay Loading */}
            {overlayLoading.length > 0 && (
                <div className="loading-overlay-container">
                    {overlayLoading.map((loading) => (
                        <LoadingItem
                            key={loading.id}
                            loading={loading}
                            onCancel={handleCancel}
                        />
                    ))}
                </div>
            )}

            {/* Inline Loading */}
            {inlineLoading.length > 0 && (
                <div className="loading-inline-container">
                    {inlineLoading.map((loading) => (
                        <LoadingItem
                            key={loading.id}
                            loading={loading}
                            onCancel={handleCancel}
                        />
                    ))}
                </div>
            )}
        </>
    );
});

LoadingContainer.displayName = 'LoadingContainer';

export default LoadingContainer;